import { Component, EventEmitter, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { forkJoin, Subject, switchMap, takeUntil } from 'rxjs';
import { SelectionItem } from '../../../core/component/select/select.component';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { LanguageService } from '../../../core/service/language.service';
import { LettingFeature } from '../../model/letting-feature';
import { LettingFeatureService } from '../../service/data/letting-feature.service';

const IS_INCLUDED_CONTROL = 'isIncluded';
const ID_CONTROL = 'id';
const LETTING_FEATURE_ID_CONTROL = 'lettingFeatureId';
const ROW_KEY_CONTROL = 'rowKey';

@Component({
  selector: 'app-letting-features-form',
  templateUrl: './letting-features-form.component.html',
  styleUrls: ['./letting-features-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LettingFeaturesFormComponent implements OnInit {
  private _forms: FormArray;
  @Input() set forms(value: AbstractControl) {
    this._forms = value as FormArray;
  }
  get forms(): FormArray {
    return this._forms;
  }

  get formGroups(): FormGroup[] {
    return (this.forms?.controls ?? []) as FormGroup[];
  }

  @Input() dataContext: { destinationIds: string[] };
  @Input() submitEvent: EventEmitter<void>;

  private _savedLettingFeatures: { included: LettingFeature[]; excluded: LettingFeature[] } = { included: [], excluded: [] };
  private _addedIncludedLettingFeatureIds: string[] = [];
  private _addedNotIncludedLettingFeatureIds: string[] = [];
  private _removedFeatures: string[] = [];
  private _availableLettingFeatures: LettingFeature[] = [];
  private _selectionItemsByGroup = new Map<FormGroup, SelectionItem<string>[]>();
  private _nextRowId = 0;

  private _destroy$ = new Subject<void>();
  private _working = false;
  private _initialized: boolean = false;

  constructor(
    private _service: LettingFeatureService,
    private _fb: FormBuilder,
    private _fcf: FormControlExFactory,
    private _languageService: LanguageService
  ) {}

  ngOnInit() {
    forkJoin({
      usedFeatures: this._service.getUsedFeatures(this.dataContext.destinationIds),
      availableFeatures: this._service.getAvailableForRentalObjects(),
    }).subscribe(({ usedFeatures, availableFeatures }) => {
      this._availableLettingFeatures = availableFeatures;
      this._initializeFormArray(usedFeatures.usedIncludedFeatures, usedFeatures.usedNotIncludedFeatures);
      this._initialized = true;
    });

    this.submitEvent.pipe(takeUntil(this._destroy$)).subscribe({ next: () => this.submit() });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
  }

  submit() {
    if (this._working || !this._initialized) return;

    this._processForms();
    if (this._isFeaturesArrayEmpty()) return;

    this._working = true;
    this._service
      .updateRentalObjectFeatures(this._buildModel())
      .pipe(switchMap(() => this._service.getUsedFeatures(this.dataContext.destinationIds)))
      .subscribe(res => {
        this._handleSuccess(res.usedIncludedFeatures, res.usedNotIncludedFeatures);
      });
  }

  removeFeature(index: number) {
    const form = this.forms.at(index) as FormGroup;
    const id = form.get(ID_CONTROL).value;
    if (id) {
      this._removeFeature(id);
      this.forms.markAsDirty();
    }

    this._selectionItemsByGroup.delete(form);
    this.forms.removeAt(index);
  }

  addFeature() {
    this.forms.push(this._createLettingFeatureGroup(new LettingFeature(), true));
    this.forms.markAsDirty();
  }

  getFeatureSelectionItems(group: FormGroup): SelectionItem<string>[] {
    const existingItems = this._selectionItemsByGroup.get(group);
    if (existingItems) {
      return existingItems;
    }

    const items = this._availableLettingFeatures.map(feature => {
      const caption = this._getFeatureCaption(feature);
      const name = feature.isActive ? caption : `${caption} (${this._languageService.instant('LETTING_FEATURE.INACTIVE')})`;
      const isDisabled = () => !feature.isActive || this._isSelectedByAnotherRow(feature.id, group);
      return {
        name,
        value: feature.id,
        get disabled() {
          return isDisabled();
        },
      } as SelectionItem<string>;
    });
    this._selectionItemsByGroup.set(group, items);
    return items;
  }

  private _handleSuccess(usedIncludedFeatures: LettingFeature[], usedNotIncludedFeatures: LettingFeature[]): void {
    this._initializeFormArray(usedIncludedFeatures, usedNotIncludedFeatures);
    this._working = false;
  }

  private _resetFeatures(): void {
    this._savedLettingFeatures.included = [];
    this._savedLettingFeatures.excluded = [];
    this._addedIncludedLettingFeatureIds = [];
    this._addedNotIncludedLettingFeatureIds = [];
    this._removedFeatures = [];
  }

  private _isFeaturesArrayEmpty(): boolean {
    return (
      this._addedIncludedLettingFeatureIds.length === 0 &&
      this._addedNotIncludedLettingFeatureIds.length === 0 &&
      this._removedFeatures.length === 0
    );
  }

  private _processForms(): void {
    this.forms.controls.forEach(control => {
      const form = control as FormGroup;
      const isIncluded = form.get(IS_INCLUDED_CONTROL).value;
      const id = form.get(ID_CONTROL).value;
      const lettingFeatureId = form.get(LETTING_FEATURE_ID_CONTROL).value;

      if (!lettingFeatureId) {
        form.get(LETTING_FEATURE_ID_CONTROL).markAsTouched();
        return;
      }

      const feature =
        this._savedLettingFeatures.included.find(f => f.id === id) ?? this._savedLettingFeatures.excluded.find(f => f.id === id);

      if (feature) {
        if (feature.lettingFeatureId !== lettingFeatureId) {
          this._removeFeature(id);
          this._addFeature(lettingFeatureId, isIncluded);
        } else {
          //Removes and readds the feature if isIncluded is changed.
          const wasIncluded = this._savedLettingFeatures.included.some(f => f.id === id);
          if (isIncluded !== wasIncluded) {
            this._removeFeature(id);
            this._addFeature(lettingFeatureId, isIncluded);
          }
        }
      } else {
        this._addFeature(lettingFeatureId, isIncluded);
      }
    });
  }

  private _buildModel() {
    return {
      removedFeatures: this._removedFeatures,
      addedIncludedFeatures: [],
      addedNotIncludedFeatures: [],
      addedIncludedLettingFeatureIds: this._addedIncludedLettingFeatureIds,
      addedNotIncludedLettingFeatureIds: this._addedNotIncludedLettingFeatureIds,
      rentalObjectIds: this.dataContext.destinationIds,
    };
  }

  private _initializeFormArray(usedIncludedFeatures: LettingFeature[], usedNotIncludedFeatures: LettingFeature[]): void {
    this.forms.clear();
    this._selectionItemsByGroup.clear();
    this._resetFeatures();

    this._savedLettingFeatures.included = usedIncludedFeatures;
    this._savedLettingFeatures.excluded = usedNotIncludedFeatures;

    this._savedLettingFeatures.included.forEach(feature => {
      this.forms.push(this._createLettingFeatureGroup(feature, true));
    });

    this._savedLettingFeatures.excluded.forEach(feature => {
      this.forms.push(this._createLettingFeatureGroup(feature, false));
    });
  }

  private _createLettingFeatureGroup(lettingFeature: LettingFeature, isIncluded: boolean): FormGroup {
    const form = this._fb.group({
      lettingFeatureId: this._fcf.createWithOptions('RENTAL_OBJECT.LETTING_FEATURES.FEATURE', lettingFeature.lettingFeatureId, {
        required: true,
      }),
      isIncluded: this._fcf.create('RENTAL_OBJECT.LETTING_FEATURES.INCLUDED', isIncluded),
      id: this._fb.control(lettingFeature.id),
      [ROW_KEY_CONTROL]: this._fb.control(lettingFeature.id || `new-${++this._nextRowId}`),
    });

    if (!lettingFeature.isNew && !lettingFeature.isActive) {
      form.get(LETTING_FEATURE_ID_CONTROL).disable();
      form.get(IS_INCLUDED_CONTROL).disable();
    }

    return form;
  }

  private _addFeature(lettingFeatureId: string, isIncluded: boolean) {
    if (isIncluded) {
      this._addedIncludedLettingFeatureIds.push(lettingFeatureId);
    } else {
      this._addedNotIncludedLettingFeatureIds.push(lettingFeatureId);
    }
  }

  private _removeFeature(id: string) {
    this._removedFeatures.push(id);
  }

  private _isSelectedByAnotherRow(lettingFeatureId: string, group: FormGroup): boolean {
    return this.forms.controls.some(control => control !== group && control.get(LETTING_FEATURE_ID_CONTROL).value === lettingFeatureId);
  }

  private _getFeatureCaption(feature: LettingFeature): string {
    return (
      feature.phrase.find(x => x.cultureName === this._languageService.currentCultureLang)?.translationText ??
      feature.phrase.find(x => !!x.translationText)?.translationText ??
      ''
    );
  }
}
